import {
  IRaffleScheduledDrawDTO,
  IRaffleScheduledDrawTicketDTO,
} from "@hooks/dtos/raffles/IRaffleDefaultDrawScheduleDTO";
import {
  RaffleDefaultDrawScheduleStatusEnum,
  RaffleDefaultDrawScheduleTypeEnum,
  RaffleDefaultDrawScheduleTypes,
} from "@hooks/enums/raffle_default_draw_schedule";
import {
  getRaffleDefaultDrawScheduleStatus,
  getRaffleDefaultDrawScheduleType,
} from "@hooks/functions/Raffle/status";
import { formatName } from "@hooks/functions/formatName";
import dayjs from "dayjs";

interface IProps {
  type?: RaffleDefaultDrawScheduleTypes | null;
  schedule: IRaffleScheduledDrawDTO;
}

function formatDateTime(date?: string | null) {
  if (!date || !dayjs(date).isValid()) return "-";

  return dayjs(date).format("DD/MM/YYYY [às] HH:mm");
}

function getWinnerName(ticket?: IRaffleScheduledDrawTicketDTO | null) {
  const name = ticket?.clientData?.name;
  if (name) return formatName(name);

  return "-";
}

function getWinnerAtSign(ticket?: IRaffleScheduledDrawTicketDTO | null) {
  const atSign = ticket?.clientData?.atSign;
  if (atSign) return `@${atSign}`;

  return "-";
}

export function ModalRaffleOwnerScheduleSummary({ type, schedule }: IProps) {
  const scheduleTypeValue = type || schedule.type;
  const scheduleStatus = getRaffleDefaultDrawScheduleStatus(schedule.status);
  const scheduleType = getRaffleDefaultDrawScheduleType(scheduleTypeValue);
  const lottery = schedule.lottery;
  const random = schedule.random;
  const winnerTicket =
    schedule.winner?.ticketData ||
    schedule.winnerTicketData ||
    schedule.winnerOrderData;
  const winnerOrder =
    schedule.winner?.order || schedule.winnerOrder || winnerTicket?.order;

  return (
    <div className="border rounded p-3">
      <div className="d-flex flex-column flex-md-row justify-content-between gap-3 mb-3">
        <div>
          <h6 className="fw-bold mb-1">Agendamento encontrado</h6>
          <small className="text-muted">
            Esta campanha já possui um sorteio registrado.
          </small>
        </div>
        <span
          className={`badge bg-${scheduleStatus.color} align-self-start`}
          title={scheduleStatus.title}
        >
          {scheduleStatus.text}
        </span>
      </div>

      <div className="row g-3">
        <div className="col-md-4">
          <div className="bg-light rounded p-3 h-100">
            <small className="text-muted">Tipo</small>
            <div className="fw-bold" title={scheduleType.title}>
              {scheduleType.text}
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="bg-light rounded p-3 h-100">
            <small className="text-muted">Agendado para</small>
            <div className="fw-bold">
              {scheduleTypeValue === RaffleDefaultDrawScheduleTypeEnum.LOTTERY
                ? `Concurso ${
                    lottery?.contestNumber || schedule.contestNumber || "-"
                  }`
                : formatDateTime(random?.drawAt || schedule.drawAt)}
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="bg-light rounded p-3 h-100">
            <small className="text-muted">Criado em</small>
            <div className="fw-bold">{formatDateTime(schedule.createdAt)}</div>
          </div>
        </div>
      </div>

      {scheduleTypeValue === RaffleDefaultDrawScheduleTypeEnum.LOTTERY && (
        <div className="row g-3 mt-1">
          <div className="col-md-4">
            <div className="border rounded p-3 h-100">
              <small className="text-muted">Combinação</small>
              <div className="fw-bold">
                {lottery?.lotteryCombination ||
                  schedule.lotteryCombination ||
                  "-"}
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="border rounded p-3 h-100">
              <small className="text-muted">Número sorteado</small>
              <div className="fw-bold">
                {lottery?.lotteryNumber || schedule.lotteryNumber || "-"}
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="border rounded p-3 h-100">
              <small className="text-muted">Processado em</small>
              <div className="fw-bold">
                {formatDateTime(schedule.processedAt)}
              </div>
            </div>
          </div>
        </div>
      )}

      {schedule.status === RaffleDefaultDrawScheduleStatusEnum.DONE &&
        winnerTicket && (
          <div className="alert alert-success mt-3 mb-0">
            <div className="d-flex flex-column flex-md-row justify-content-between gap-2 mb-3">
              <div>
                <div className="fw-bold">Resultado definido</div>
                <small>
                  O sorteio foi processado e o ticket vencedor já foi
                  identificado.
                </small>
              </div>
              {schedule.processedAt && (
                <small title="Data em que o resultado foi processado.">
                  Processado em {formatDateTime(schedule.processedAt)}
                </small>
              )}
            </div>

            <div className="row g-2">
              <div className="col-md-5">
                <div className="bg-white rounded p-3 h-100">
                  <small className="text-muted">Ganhador</small>
                  <div className="fw-bold" title="Nome do comprador vencedor.">
                    {getWinnerName(winnerTicket)}
                  </div>
                  <div
                    className="text-primary small fw-semibold"
                    title="@arroba do comprador vencedor."
                  >
                    {getWinnerAtSign(winnerTicket)}
                  </div>
                </div>
              </div>
              <div className="col-md-7">
                <div className="row g-2">
                  <div className="col-6 col-md-4">
                    <div className="bg-white rounded p-3 h-100">
                      <small className="text-muted">Ordem vencedora</small>
                      <div
                        className="fw-bold"
                        title="Número calculado pelo resultado do sorteio."
                      >
                        {winnerOrder || "-"}
                      </div>
                    </div>
                  </div>
                  <div className="col-6 col-md-4">
                    <div className="bg-white rounded p-3 h-100">
                      <small className="text-muted">Ticket</small>
                      <div
                        className="fw-bold"
                        title="Ticket comprado que venceu a campanha."
                      >
                        {winnerTicket.ticket || "-"}
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-md-4">
                    <div className="bg-white rounded p-3 h-100">
                      <small className="text-muted">Ticket ID</small>
                      <div
                        className="fw-bold"
                        title="Identificador interno do ticket vencedor."
                      >
                        {schedule.winner?.ticketId || winnerTicket.id || "-"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {winnerTicket.boughtAt && (
              <small className="d-block mt-2">
                Compra realizada em {formatDateTime(winnerTicket.boughtAt)}.
              </small>
            )}
          </div>
        )}

      {schedule.status === RaffleDefaultDrawScheduleStatusEnum.FAILED && (
        <div className="alert alert-danger mt-3 mb-0">
          {schedule.message || "Ocorreu um erro ao processar o sorteio."}
        </div>
      )}

      {schedule.status === RaffleDefaultDrawScheduleStatusEnum.CANCELED && (
        <div className="alert alert-secondary mt-3 mb-0">
          Este agendamento foi cancelado.
        </div>
      )}
    </div>
  );
}
